import React, { useMemo } from 'react';
import Gantt from './Gantt';
import AyaLogo from './AyaLogo';
import {
  STATUS_LABELS,
  STATUS_BADGE_CLASS,
  PROJECT_STATUS_LABELS,
  PRIORITY_LABELS,
  projectCode,
  taskCode,
  userDisplayName,
  formatDate,
  formatMontant,
  avancementTache,
  isOverdue,
  joursDeRetard,
  libelleType,
  libelleService,
  joursDeBlocage,
  niveauEscalade,
  ESCALADE_SERVICE_JOURS,
  ESCALADE_DIRECTION_JOURS,
} from './helpers';

const STATUS_ORDER = ['Todo', 'In_Progress', 'In_Review', 'Done', 'Blocked'];

// Couleurs alignées sur celles des barres du Gantt, pour que l'œil fasse le
// lien entre la répartition et le planning.
const STATUS_COLOR = {
  Todo: '#8CA0B3',
  In_Progress: '#517997',
  In_Review: '#C19E54',
  Done: '#4CAF50',
  Blocked: '#D9534F',
};

const HORIZON_JOURS = 14;

function ProjectReport({ project, tasks: toutes, users, deliverableCount }) {
  const aujourdhui = new Date();
  // Les jalons sont des repères de date : ils ont leur propre section et ne
  // comptent pas dans les statistiques de tâches.
  const tasks = useMemo(() => toutes.filter((t) => !t.est_jalon), [toutes]);
  const jalons = useMemo(
    () =>
      toutes
        .filter((t) => t.est_jalon)
        .map((j) => {
          const rattachees = toutes.filter((t) => !t.est_jalon && t.jalon_id === j.id);
          const termine = rattachees.filter((t) => t.status === 'Done').length;
          const echeance = j.due_date ? String(j.due_date).slice(0, 10) : null;
          const jour = new Date().toISOString().slice(0, 10);
          const atteint =
            j.status === 'Done' || (rattachees.length > 0 && termine === rattachees.length);
          return {
            ...j,
            total: rattachees.length,
            termine,
            atteint,
            depasse: !!echeance && echeance < jour && !atteint,
            avancement: rattachees.length
              ? Math.round(rattachees.reduce((sm, t) => sm + avancementTache(t), 0) / rattachees.length)
              : j.status === 'Done'
              ? 100
              : 0,
          };
        })
        .sort((a, b) => String(a.due_date || '9999').localeCompare(String(b.due_date || '9999'))),
    [toutes]
  );

  const analyse = useMemo(() => {
    const counts = {};
    STATUS_ORDER.forEach((s) => {
      counts[s] = tasks.filter((t) => t.status === s).length;
    });

    const enRetard = tasks.filter(isOverdue).sort((a, b) => joursDeRetard(b) - joursDeRetard(a));

    const jour = new Date().toISOString().slice(0, 10);
    const limite = new Date(Date.now() + HORIZON_JOURS * 86400000).toISOString().slice(0, 10);
    const aVenir = tasks
      .filter((t) => {
        if (t.status === 'Done' || !t.due_date) return false;
        const d = String(t.due_date).slice(0, 10);
        return d >= jour && d <= limite;
      })
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));

    // Blocages, du plus ancien au plus récent : c'est l'ordre dans lequel il
    // faut les traiter en revue de projet.
    const bloquees = tasks
      .filter((t) => t.status === 'Blocked')
      .map((t) => ({ ...t, jours: joursDeBlocage(t) }))
      .sort((a, b) => b.jours - a.jours);

    return { counts, enRetard, aVenir, bloquees };
  }, [tasks]);

  const manager = users.find((u) => u.id === project.project_manager_id);
  const budgetPct = project.budget_allocated
    ? Math.round(((project.budget_spent || 0) / project.budget_allocated) * 100)
    : 0;
  const nomDe = (id) => userDisplayName(users.find((u) => u.id === id));

  const terminees = analyse.counts.Done || 0;
  const ecartBudget = project.budget_allocated ? budgetPct - (project.progress_percent || 0) : null;

  return (
    <div className="rapport">
      {/* Barre d'action : masquée à l'impression. */}
      <div className="page-header-row rapport-actions">
        <h2 style={{ margin: 0 }}>Rapport d’avancement</h2>
        <button className="btn btn-primary" onClick={() => window.print()}>Exporter en PDF</button>
      </div>

      {/* En-tête de document. Donne une identité au PDF : sans lui, la première
          page commençait directement par un tableau. */}
      <div className="rapport-entete">
        <AyaLogo className="rapport-logo" />
        <div>
          <div className="rapport-surtitre">AYA Gold &amp; Silver — Zgounder</div>
          <div className="rapport-titre">Rapport d’avancement — {project.name}</div>
          <div className="rapport-soustitre">
            {projectCode(project.id)} · {PROJECT_STATUS_LABELS[project.status] || project.status}
            {' '}· Édité le {formatDate(aujourdhui)}
          </div>
        </div>
      </div>

      {/* --- 1. Synthèse ---------------------------------------------------- */}
      <section className="rapport-section">
        <h3 className="rapport-section-titre">Synthèse</h3>

        <div className="rapport-kpis">
          <div className="rapport-kpi rapport-kpi-vedette">
            <div className="rapport-kpi-label">Avancement</div>
            <div className="rapport-kpi-valeur">{project.progress_percent || 0}%</div>
            <div className="rapport-jauge">
              <div
                className="rapport-jauge-remplissage"
                style={{ width: `${project.progress_percent || 0}%` }}
              />
            </div>
            <div className="rapport-kpi-sous">
              {terminees} tâche{terminees > 1 ? 's' : ''} terminée{terminees > 1 ? 's' : ''} sur {tasks.length}
            </div>
          </div>

          <div className="rapport-kpi">
            <div className="rapport-kpi-label">Tâches ouvertes</div>
            <div className="rapport-kpi-valeur">{tasks.length - terminees}</div>
            <div className="rapport-kpi-sous">{tasks.length} au total</div>
          </div>

          <div className="rapport-kpi">
            <div className="rapport-kpi-label">En retard</div>
            <div
              className="rapport-kpi-valeur"
              style={{ color: analyse.enRetard.length ? '#D9534F' : '#4CAF50' }}
            >
              {analyse.enRetard.length}
            </div>
            <div className="rapport-kpi-sous">
              {analyse.enRetard.length ? 'échéance dépassée' : 'aucun retard'}
            </div>
          </div>

          <div className="rapport-kpi">
            <div className="rapport-kpi-label">Échéance projet</div>
            <div className="rapport-kpi-valeur rapport-kpi-date">{formatDate(project.end_date)}</div>
            <div className="rapport-kpi-sous">début {formatDate(project.start_date)}</div>
          </div>
        </div>

        <table className="rapport-fiche">
          <tbody>
            <tr>
              <th>Chef de projet</th>
              <td>{userDisplayName(manager)}</td>
              <th>Livrables déposés</th>
              <td>{deliverableCount}</td>
            </tr>
            <tr>
              <th>Type de projet</th>
              <td>{libelleType(project.project_type) || 'Non classé'}</td>
              <th>Service pilote</th>
              <td>{libelleService(project.project_service) || 'Non affecté'}</td>
            </tr>
            <tr>
              <th>Budget alloué</th>
              <td>{project.budget_allocated ? formatMontant(project.budget_allocated) : 'Non renseigné'}</td>
              <th>Budget engagé</th>
              <td>{formatMontant(project.budget_engaged || 0)}</td>
            </tr>
            <tr>
              <th>Budget dépensé</th>
              <td>
                {formatMontant(project.budget_spent || 0)}
                {project.budget_allocated ? ` (${budgetPct}%)` : ''}
              </td>
              <th>Reste disponible</th>
              <td>{project.budget_allocated ? formatMontant(project.budget_remaining || 0) : '—'}</td>
            </tr>
          </tbody>
        </table>

        {project.description && <p className="rapport-description">{project.description}</p>}

        {ecartBudget !== null && Math.abs(ecartBudget) >= 15 && (
          <p className={`rapport-alerte ${ecartBudget > 0 ? 'rapport-alerte-rouge' : ''}`}>
            {ecartBudget > 0
              ? `Vigilance : ${ecartBudget} points de budget consommés de plus que d’avancement réalisé.`
              : `L’avancement dépasse la consommation budgétaire de ${Math.abs(ecartBudget)} points.`}
          </p>
        )}
      </section>

      {/* --- 2. Répartition ------------------------------------------------- */}
      <section className="rapport-section rapport-eviter-coupure">
        <h3 className="rapport-section-titre">Répartition des tâches</h3>
        <div className="rapport-repartition">
          {STATUS_ORDER.map((s) => {
            const n = analyse.counts[s] || 0;
            const pct = tasks.length ? (n / tasks.length) * 100 : 0;
            return (
              <div key={s} className="rapport-repartition-ligne">
                <span className="rapport-repartition-label">{STATUS_LABELS[s]}</span>
                <div className="rapport-jauge">
                  <div
                    className="rapport-jauge-remplissage"
                    style={{ width: `${pct}%`, background: STATUS_COLOR[s] }}
                  />
                </div>
                <span className="rapport-repartition-valeur">
                  {n} <span className="rapport-repartition-pct">({Math.round(pct)}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {jalons.length > 0 && (
        <section className="rapport-section">
          <h3 className="rapport-section-titre">
            Jalons ({jalons.filter((j) => j.atteint).length}/{jalons.length} atteints)
          </h3>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th>Jalon</th>
                <th>Date visée</th>
                <th className="rapport-col-droite">Tâches</th>
                <th className="rapport-col-droite">Avancement</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {jalons.map((j) => (
                <tr key={j.id}>
                  <td>◆ {j.name}</td>
                  <td>{formatDate(j.due_date)}</td>
                  <td className="rapport-col-droite">{j.total ? `${j.termine}/${j.total}` : '—'}</td>
                  <td className="rapport-col-droite">{j.avancement}%</td>
                  <td className={j.depasse ? 'rapport-retard' : ''}>
                    {j.atteint ? 'Atteint' : j.depasse ? 'Dépassé' : 'À venir'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* --- 3. Points d'attention ------------------------------------------ */}
      {analyse.bloquees.length > 0 && (
        <section className="rapport-section">
          <h3 className="rapport-section-titre">Blocages ({analyse.bloquees.length})</h3>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th className="rapport-col-ref">Réf.</th>
                <th>Tâche</th>
                <th>On attend</th>
                <th>De qui</th>
                <th>Responsable</th>
                <th className="rapport-col-droite">Depuis</th>
              </tr>
            </thead>
            <tbody>
              {analyse.bloquees.map((t) => (
                <tr key={t.id}>
                  <td className="rapport-ref">{taskCode(t.id)}</td>
                  <td>{t.name}</td>
                  <td>{t.blocage_motif || '—'}</td>
                  <td>{t.blocage_qui || '—'}</td>
                  <td>{nomDe(t.delegated_to || t.assigned_to)}</td>
                  <td
                    className={`rapport-col-droite${niveauEscalade(t.jours) ? ' rapport-retard' : ''}`}
                  >
                    {t.jours} j
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rapport-note">
            Un blocage non levé remonte automatiquement au chef de service
            à {ESCALADE_SERVICE_JOURS} jours, puis à la direction d’usines
            à {ESCALADE_DIRECTION_JOURS} jours.
          </p>
        </section>
      )}

      {analyse.enRetard.length > 0 && (
        <section className="rapport-section">
          <h3 className="rapport-section-titre">Tâches en retard ({analyse.enRetard.length})</h3>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th className="rapport-col-ref">Réf.</th>
                <th>Tâche</th>
                <th>Responsable</th>
                <th>Échéance</th>
                <th className="rapport-col-droite">Retard</th>
              </tr>
            </thead>
            <tbody>
              {analyse.enRetard.slice(0, 15).map((t) => (
                <tr key={t.id}>
                  <td className="rapport-ref">{taskCode(t.id)}</td>
                  <td>{t.name}</td>
                  <td>{nomDe(t.delegated_to || t.assigned_to)}</td>
                  <td>{formatDate(t.due_date)}</td>
                  <td className="rapport-col-droite rapport-retard">{joursDeRetard(t)} j</td>
                </tr>
              ))}
            </tbody>
          </table>
          {analyse.enRetard.length > 15 && (
            <p className="rapport-note">
              Les {analyse.enRetard.length - 15} autres tâches en retard figurent dans le tableau détaillé.
            </p>
          )}
        </section>
      )}

      {analyse.aVenir.length > 0 && (
        <section className="rapport-section">
          <h3 className="rapport-section-titre">
            Échéances des {HORIZON_JOURS} prochains jours ({analyse.aVenir.length})
          </h3>
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th className="rapport-col-ref">Réf.</th>
                <th>Tâche</th>
                <th>Responsable</th>
                <th>Priorité</th>
                <th>Échéance</th>
              </tr>
            </thead>
            <tbody>
              {analyse.aVenir.slice(0, 15).map((t) => (
                <tr key={t.id}>
                  <td className="rapport-ref">{taskCode(t.id)}</td>
                  <td>{t.name}</td>
                  <td>{nomDe(t.delegated_to || t.assigned_to)}</td>
                  <td>{PRIORITY_LABELS[t.priority] || t.priority || '—'}</td>
                  <td>{formatDate(t.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* --- 4. Planning ---------------------------------------------------- */}
      {/* Le planning tient sur quelques lignes : on le garde d'un seul tenant,
          mais sans forcer une page pour lui — il se plaçait seul sur une feuille
          aux trois quarts vide. */}
      <section className="rapport-section rapport-eviter-coupure">
        <h3 className="rapport-section-titre">Planning du projet</h3>
        <Gantt tasks={toutes} users={users} compact />
      </section>

      {/* --- 5. Détail ------------------------------------------------------ */}
      {/* Le détail est un long tableau : il s'enchaîne à la suite et se
          poursuit page après page, en-tête répété. */}
      <section className="rapport-section">
        <h3 className="rapport-section-titre">Détail des tâches ({tasks.length})</h3>
        <table className="rapport-tableau rapport-tableau-detail">
          <thead>
            <tr>
              <th className="rapport-col-ref">Réf.</th>
              <th>Tâche</th>
              <th>Responsable</th>
              <th>Priorité</th>
              <th>Statut</th>
              <th>Échéance</th>
              <th className="rapport-col-droite">Avanc.</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td className="rapport-ref">{taskCode(t.id)}</td>
                <td>{t.name}</td>
                <td>{nomDe(t.assigned_to)}</td>
                <td>{PRIORITY_LABELS[t.priority] || t.priority || '—'}</td>
                <td>
                  <span className={`badge ${STATUS_BADGE_CLASS[t.status] || 'badge-todo'}`}>
                    {STATUS_LABELS[t.status] || t.status}
                  </span>
                </td>
                <td className={isOverdue(t) ? 'rapport-retard' : ''}>{formatDate(t.due_date)}</td>
                <td className="rapport-col-droite">{avancementTache(t)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Pied de page : répété sur chaque feuille grâce à position: fixed. */}
      <div className="rapport-pied">
        VIGIE · {projectCode(project.id)} — {project.name} · Édité le {formatDate(aujourdhui)}
      </div>
    </div>
  );
}

export default ProjectReport;
